import { motion } from 'framer-motion';
import { FC } from 'react';

import {
  Button,
  Caption,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSelectableItem,
  DropdownMenuTrigger,
  FCC,
  Icon,
  LoadingIcon,
  Stack,
  Text,
  cn,
} from '@eluve/components';
import {
  type AppointmentTab,
  SUMMARY_TAB,
  TRANSCRIPT_TAB,
  useAppointmentTabNav,
} from '@eluve/frontend-appointment-hooks';
import { useTranslation } from '@eluve/i18n';

export interface TabConfig {
  tab: AppointmentTab;
  label: string;
  loading?: boolean;
  disabled?: boolean;
}

interface AppointmentTabsProps {
  tabs: TabConfig[];
  compact?: boolean;
  activeTab?: AppointmentTab;
  onTabSelect?: (tab: AppointmentTab) => void;
}

export const AppointmentTabs: FC<AppointmentTabsProps> = ({
  tabs,
  compact = false,
  activeTab,
  onTabSelect,
}) => {
  const { setTab, tab } = useAppointmentTabNav();
  const resolvedActiveTab = activeTab ?? tab;
  const handleTabSelect = onTabSelect ?? setTab;
  const activeIndex = tabs.findIndex((t) => t.tab === resolvedActiveTab);

  return (
    <Stack
      className={cn(
        'relative overflow-hidden border border-border-primary bg-bg-tertiary',
        compact ? 'rounded-md p-px' : 'rounded-lg p-0.5',
      )}
      direction="horizontal"
    >
      <div
        className={cn(
          'absolute z-0 border border-border-primary bg-bg-primary',
          compact
            ? 'inset-y-px left-px rounded-sm'
            : 'inset-y-0.5 left-0.5 rounded-md',
        )}
        style={{
          width: `calc((100% - ${compact ? 2 : 4}px) / ${tabs.length})`,
          transform: `translateX(${activeIndex * 100}%)`,
          transition: 'transform 400ms cubic-bezier(0.25, 1, 0.5, 1)',
        }}
      />
      {tabs.map((tabConfig) => (
        <button
          key={tabConfig.tab}
          className={cn(
            'relative z-10 flex-1',
            compact ? 'p-1.5' : 'p-2',
            tabConfig.disabled && 'cursor-not-allowed',
          )}
          disabled={tabConfig.disabled}
          onClick={() => handleTabSelect(tabConfig.tab)}
        >
          <Stack justify="center" gap={2} direction="horizontal">
            {tabConfig.loading && <LoadingIcon />}
            <Text
              inline
              className="transition-colors duration-200"
              color={
                resolvedActiveTab === tabConfig.tab
                  ? 'primary'
                  : tabConfig.disabled
                    ? 'disabled'
                    : 'supporting'
              }
              size={compact ? 'xs' : 'md'}
              weight={compact ? 'semibold' : 'regular'}
            >
              {tabConfig.label}
            </Text>
          </Stack>
        </button>
      ))}
    </Stack>
  );
};

interface AppointmentTabsDropdownProps {
  disabled?: boolean;
}

export const AppointmentTabsDropdown: FC<AppointmentTabsDropdownProps> = ({
  disabled,
}) => {
  const { t } = useTranslation();
  const { setTab, tab } = useAppointmentTabNav();

  return (
    <Stack className="p-4" gap={2} direction="horizontal">
      <Caption inline color="supporting" size="2xs" weight="bold">
        {tab === TRANSCRIPT_TAB
          ? t('appointment.transcriptTab')
          : t('appointment.soapNotesTab')}
      </Caption>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger disabled={disabled} asChild>
          <Button variant="ghost" size="sm">
            <Icon name="ChevronDown" size="auto" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          onCloseAutoFocus={(event) => event.preventDefault()}
          usesPortal={false}
        >
          <DropdownMenuSelectableItem
            currentValue={tab}
            onClick={() => setTab(TRANSCRIPT_TAB)}
            text={t('appointment.transcriptTab')}
            value={TRANSCRIPT_TAB}
          />
          <DropdownMenuSelectableItem
            currentValue={tab}
            onClick={() => setTab(SUMMARY_TAB)}
            text={t('appointment.soapNotesTab')}
            value={SUMMARY_TAB}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </Stack>
  );
};

type AppointmentTabContentProps = {
  tab: AppointmentTab | AppointmentTab[];
  className?: string;
};

export const AppointmentTabContent: FCC<AppointmentTabContentProps> = ({
  tab,
  children,
  className,
}) => {
  const { tab: currentTab } = useAppointmentTabNav();

  const isActive = Array.isArray(tab)
    ? tab.includes(currentTab)
    : tab === currentTab;

  if (!isActive) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className={cn('flex w-full flex-1 flex-col gap-0', className)}
    >
      {children}
    </motion.div>
  );
};
